// WHEN AN OPEN FEELS LOADED AND HAS SETTLED: one definition for the page-speed
// harness (tools/page-speed/settle.mjs) and the browser's own beacon, so the rig
// and prod can't drift apart. No server import, no browser API: the caller
// hands it the times it observed, all in ms from the click (or navigation start).
//
// Feels loaded is the paint of the largest block the click inserted (LCP's
// rule, applied to a click), unless the page marks its primary content with a
// `data-primary` attribute: then it is the last paint inside that element, for
// a page whose largest block is the wrong one (a big empty-state panel over a
// small table). Settled is the earliest moment, at or after feels
// loaded, from which every layout shift still to come adds up to <= 0.1, CLS's
// "good" line. A top row that arrives a second later and pushes the page down
// makes settled a second later; a shift before feels loaded is the page being
// built, and doesn't count.
//
// Shifts are the browser's `layout-shift` entries INCLUDING those flagged
// `hadRecentInput` (within 500 ms of the click): Chrome's own CLS leaves them
// out, and an in-app click is exactly that case. Chromium only; where the
// browser reports no shifts at all, settled = feels loaded and the caller says
// so. The caller collects until the page is still (SETTLED_STILL_MS of no paint
// and no shift) or SETTLED_COLLECT_MAX_MS.
//
// ⚠️ PLAIN JAVASCRIPT, ZERO IMPORTS: tools/page-speed/settle.mjs runs it under
// bare node, and the app's client code imports it as it is.

/** The shift still to come that an open may have and count as settled. */
export const SETTLED_SHIFT_MAX = 0.1;
/** The page is still after this long with no paint and no shift. */
export const SETTLED_STILL_MS = 2000;
/** Collection stops here whatever is still happening. */
export const SETTLED_COLLECT_MAX_MS = 10_000;

/**
 * One element paint (an Element Timing entry): when (ms from the click), the
 * area it painted on screen (its intersectionRect, px²), and whether it is the
 * `data-primary` element or inside one.
 * @typedef {{ t: number; area: number; primary?: boolean }} Paint
 */

/**
 * Feels loaded, ms from the click: the last on-screen paint inside a
 * `data-primary` element when there is one, else the paint of the largest
 * block (the first of equals, as LCP keeps it); null when nothing painted on
 * screen. The caller may hand it every paint or only LCP's candidates (each
 * paint larger than all before it) plus every primary one: the answer is the same.
 * @param {ReadonlyArray<Paint>} paints
 * @returns {number | null}
 */
export function feelsLoadedAt(paints) {
  let big = null;
  let primary = null;
  for (const p of paints) {
    if (!(p.area > 0)) continue; // off screen: not what the person sees
    if (p.primary) primary = primary == null ? p.t : Math.max(primary, p.t);
    else if (!big || p.area > big.area) big = p;
  }
  return primary ?? (big ? big.t : null);
}

/**
 * One `layout-shift` entry: when (ms from the click) and its score.
 * @typedef {{ t: number; value: number }} Shift
 */

/**
 * settled: ms from the click, null when nothing painted (no feels loaded);
 * shiftAfter: every shift at or after feels loaded, summed.
 * @typedef {{ settled: number | null; shiftAfter: number }} Settled
 */

/**
 * Settled and the shift after feels loaded, from feels loaded's ms and the shifts seen.
 * @param {number | null} feelsLoaded
 * @param {ReadonlyArray<Shift>} shifts
 * @returns {Settled}
 */
export function settledAt(feelsLoaded, shifts) {
  if (feelsLoaded == null) return { settled: null, shiftAfter: 0 };
  const after = shifts.filter((s) => s.t >= feelsLoaded).sort((a, b) => a.t - b.t);
  let shiftAfter = 0;
  for (const s of after) shiftAfter += s.value;
  // Walk back from the last shift: the first one that takes what is still to
  // come past the line is the moment the page settled.
  let rest = 0;
  for (let i = after.length - 1; i >= 0; i--) {
    rest += after[i].value;
    if (rest > SETTLED_SHIFT_MAX) return { settled: after[i].t, shiftAfter };
  }
  return { settled: feelsLoaded, shiftAfter };
}
